import * as React from "react";

// Third Party
import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";

import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";

// Icons
import { ArrowLeft, Save } from "lucide-react";

// Service and Utils
import { getResult, editResult } from "@/services/finance";
import type {
	CreateFinancialRequest,
} from "@/types/finance";
import type {
	FinancialDetail,
	FinancialResponse,
} from "@/types/finance";

export const Route = createFileRoute(
	"/_layout-private/report/finance/company/_layout/result/edit",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const companyId = localStorage.getItem("companyId");
	const [docFile, setDocFile] = useState("");

	const year = Number(localStorage.getItem("year"));
	const queryClient = useQueryClient();

	const { data, isLoading, error } = useQuery({
		queryKey: ["getResult", companyId, year],
		queryFn: () => getResult({ companyId: companyId as string, yearly: year }),
		enabled: Boolean(companyId && year),
	});

	const mutation = useMutation({
		mutationFn: async (formData: CreateFinancialRequest) => {
			try {
				const response = await editResult({
					companyId: formData.companyId.replace(/^"(.+)"$/, "$1"),
					yearly: formData.yearly,
					docFile: formData.docFile,
					financialDetails: formData.financialDetails.map((detail) => ({
						finDetailType: detail.finDetailType,
						financialType: detail.financialType,
						name: detail.name,
						amount: detail.amount,
						remark: detail.remark,
					})),
				});
				return response;
			} catch (error) {
				console.error("Error submitting data:", error);
				throw error;
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getResult", companyId, year],
			});
			navigate({ to: "/report/finance/company/result", search: { refId: undefined } });
			toast({
				title: "ສຳເລັດ",
				description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
				variant: "success",
			});
		},
		onError: (error) => {
			console.error("Mutation error:", error);
			toast({
				title: "ບໍ່ສຳເລັດ",
				description: "ບັນທຶກຂໍ້ມູນບໍ່ສຳເລັດ",
				variant: "destructive",
			});
		},
	});

	const [financialDetails, setFinancialDetails] = React.useState<
		Array<{
			finDetailType:
			| "INITIAL"
			| "OPERATING"
			| "PRE_TAX"
			| "NET_OPERATING"
			| "AFTER_TAX"
			| "ANNUAL_NET";
			financialType: "INCOME" | "EXPENSE";
			name: string;
			amount: number | string;
			remark: string;
			// We'll keep an extra field for the numeric amount for backend if needed.
			amountForBackend?: number;
		}>
	>([]);

	useEffect(() => {
		if (data?.data) {
			const initialFinancialDetails = data.data.financialDetails.map(
				(detail: FinancialDetail) => {
					// Determine if the amount is negative
					const isNegative = detail.financialType === "EXPENSE";

					// Format the amount string based on whether it's negative
					const formattedAmount = isNegative
						? `(${Math.abs(detail.amount)})`
						: String(detail.amount);

					return {
						finDetailType: detail.finDetailType,
						financialType: detail.financialType,
						name: detail.name,
						amount: formattedAmount,
						amountForBackend: detail.amount, // Keep the original numeric value
						remark: detail.remark,
					};
				}
			);
			setFinancialDetails(initialFinancialDetails);
			setDocFile(data.data.docFile);
		}
	}, [data]);

	if (!companyId || !year) {
		return <div>Missing data</div>;
	}

	if (isLoading) return <div>Loading...</div>;
	if (error) return <div>Error loading data</div>;


	// Remove both existing calculateTotal functions and replace with this one
	const calculateTotal = (type: string): number => {
		console.log(financialDetails, "Data Set")

		return financialDetails
			.filter((detail) => detail.finDetailType === type)
			.reduce((sum, detail) => {
				// Use the numeric value from amountForBackend if available; otherwise, fallback to 0.
				const value = detail.amountForBackend
					? Number(detail.amountForBackend)
					: 0;

				// Check if the value is already negative (for expenses)
				// This handles both initial data and edited data correctly
				if (detail.financialType === "EXPENSE" && value > 0) {
					return sum - value; // Initial data: subtract positive values for expenses
				} else {
					return sum + value; // Edited data: add values (negative for expenses, positive for income)
				}
			}, 0);
	};


	const totalInitial = React.useMemo(
		() => calculateTotal("INITIAL"),
		[financialDetails],
	);

	const totalOperating = React.useMemo(
		() => calculateTotal("OPERATING"),
		[financialDetails],
	);

	const totalPreTax = React.useMemo(
		() => calculateTotal("PRE_TAX"),
		[financialDetails],
	);

	const totalNetOperating = React.useMemo(
		() => calculateTotal("NET_OPERATING"),
		[financialDetails],
	);

	const totalAfterTax = React.useMemo(
		() => calculateTotal("AFTER_TAX"),
		[financialDetails],
	);

	// Updated formatting helper that only strips parentheses when both exist.
	const formatNumber = (value: string | number) => {
		if (!value && value !== 0) return "";

		// Convert to string and remove existing formatting
		let stringValue = String(value).replace(/[,\s]/g, "");

		// Handle parentheses
		const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
		if (isNegative) {
			stringValue = stringValue.slice(1, -1);
		}

		// Split into integer and decimal parts
		const [integerPart, decimalPart] = stringValue.split(".");

		// Format integer part with commas
		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

		// Combine with decimal part if it exists
		const formattedValue = decimalPart !== undefined
			? `${formattedInteger}.${decimalPart.slice(0, 2)}`
			: formattedInteger;

		return isNegative ? `(${formattedValue})` : formattedValue;
	};




	// Update the getSectionSUM function to ensure correct calculations
	const getSectionSUM = (type: string) => {
		const titles: Record<string, React.ReactNode> = {
			OPERATING: <p>{formatNumber(totalOperating + totalInitial)}</p>,
			PRE_TAX: (
				<p>{formatNumber(totalOperating + totalInitial + totalPreTax)}</p>
			),
			NET_OPERATING: (
				<p>
					{formatNumber(
						totalOperating + totalInitial + totalPreTax + totalNetOperating,
					)}
				</p>
			),
			AFTER_TAX: (
				<p>
					{formatNumber(totalAfterTax)}
				</p>
			),
		};
		return titles[type];
	};

	// Also update the final total calculation in the table
	<TableCell className="text-right border">
		{formatNumber(
			totalOperating +
			totalInitial +
			totalPreTax +
			totalNetOperating +
			totalAfterTax,
		)}
	</TableCell>

	// Update detail function with a cleaning routine that allows digits, parentheses, and the minus sign.
	const updateDetail = (
		type: string,
		typeIndex: number,
		field: string,
		value: string | number,
	) => {
		const newDetails = [...financialDetails];
		const actualIndex = financialDetails.findIndex(
			(detail) =>
				detail.finDetailType === type &&
				financialDetails
					.filter((d) => d.finDetailType === type)
					.indexOf(detail) === typeIndex,
		);

		if (actualIndex !== -1) {
			if (field === "amount") {
				const stringValue = String(value);

				// Allow empty input
				if (!stringValue) {
					newDetails[actualIndex] = {
						...newDetails[actualIndex],
						amount: "",
						amountForBackend: 0,
					};
					setFinancialDetails(newDetails);
					return;
				}

				// Remove existing formatting
				const rawValue = stringValue.replace(/,/g, "");

				// Check if the input matches our pattern (including decimals and parentheses)
				if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
					// Convert to number for backend
					const numericValue = rawValue.replace(/[()]/g, "");
					const numericAmount = Number(numericValue);

					newDetails[actualIndex] = {
						...newDetails[actualIndex],
						amount: rawValue,
						amountForBackend: rawValue.startsWith("(") ? -numericAmount : numericAmount,
						financialType: rawValue.startsWith("(") ? "EXPENSE" : "INCOME",
					};
					setFinancialDetails(newDetails);
				}
			} else {
				newDetails[actualIndex] = {
					...newDetails[actualIndex],
					[field]: value,
				};
				setFinancialDetails(newDetails);
			}
		}
	};

	const getDetailsByType = (type: string) =>
		financialDetails.filter((detail) => detail.finDetailType === type);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!companyId) return;

		if (!docFile) {
			toast({
				title: "ບັນທຶກລົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
				variant: "destructive",
			});
			return;
		}

		// Replace amount with the numeric version (for backend payload)
		const modifiedFinancialDetails = financialDetails.map(
			({ amountForBackend, ...rest }) => ({
				...rest,
				amount: amountForBackend ?? 0,
			}),
		);

		// Validate that every amount is a number
		const hasInvalidAmount = modifiedFinancialDetails.some(({ amount }) => {
			const numericAmount = Number(amount);
			return (
				Number.isNaN(numericAmount) || amount === null || amount === undefined
			);
		});

		if (hasInvalidAmount) {
			toast({
				title: "ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຖືກຕ້ອງ",
				description: "ກະລຸນາກວດສອບການປ້ອນຈຳນວນຕົວເລກໃນຟອມ",
				variant: "destructive",
			});
			return;
		}

		const dataToSubmit = {
			companyId,
			yearly: year!,
			docFile,
			financialDetails: modifiedFinancialDetails,
		};

		mutation.mutate(dataToSubmit);
	};

	return (
		<section>
			<Card className="border-none">
				<form onSubmit={handleSubmit}>
					<CardHeader className="border-b mb-[32px]">
						<button
							type="button"
							onClick={() =>
								navigate({
									to: "/report/finance",
									search: { page: 1, startYear: 0, endYear: 0 },
								})
							}
							className="flex items-center gap-[4px] mb-4"
						>
							<ArrowLeft className="w-5 h-5" />
							ກັບຄືນ
						</button>
						<CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {year}</CardTitle>
						<CardDescription>ລາຍລະອຽດ</CardDescription>
					</CardHeader>
					<CardContent>
						<div>
							<Table>
								<TableHeader>
									<TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black ">
										<TableHead>ເນື້ອໃນລາຍງານ</TableHead>
										<TableHead>ມູນຄ່າຍັງເຫຼືອປີນີ້</TableHead>
										<TableHead>ໝາຍເຫດ</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{getDetailsByType("INITIAL").map((detail, index) => (
										<TableRow key={index} className="[&>*]:border">
											<TableCell className="flex gap-x-1 items-center">
												{index + 1}.
												<Input
													value={detail.name}
													onChange={(e) =>
														updateDetail(
															"INITIAL",
															index,
															"name",
															e.target.value,
														)
													}
													readOnly
												/>
											</TableCell>
											<TableCell>
												{/* <Input
													type="text"
													value={formatNumber(
														detail.amount,
														detail.financialType,
													)}
													onChange={(e) => {
														const value = e.target.value;
														// Allow only digits, parentheses, commas, and minus sign
														if (/^[()\d,\-]*$/.test(value)) {
															// Remove commas for internal state update.
															const cleaned = value.replace(/,/g, "");
															updateDetail("INITIAL", index, "amount", cleaned);
														}
													}}
													className="text-right"
													inputMode="numeric"
												/> */}
												<Input
													type="text"
													value={formatNumber(detail.amount)}
													onChange={(e) => updateDetail("INITIAL", index, "amount", e.target.value)}
													className="text-right"
													inputMode="numeric"
												/>
											</TableCell>
											<TableCell>
												<Input
													value={detail.remark}
													onChange={(e) =>
														updateDetail(
															"INITIAL",
															index,
															"remark",
															e.target.value,
														)
													}
												/>
											</TableCell>
										</TableRow>
									))}
									<TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
										<TableCell>ຜົນໄດ້ຮັບເບື້ອງຕົ້ນ</TableCell>
										<TableCell className="text-right">
											{formatNumber(totalInitial)}
										</TableCell>
										<TableCell>-</TableCell>
									</TableRow>
									{["OPERATING", "PRE_TAX", "NET_OPERATING", "AFTER_TAX"].map(
										(sectionType) => (
											<React.Fragment key={sectionType}>
												{getDetailsByType(sectionType).map((detail, index) => (
													<TableRow key={index} className="[&>*]:border">
														<TableCell className="flex gap-x-1 items-center">
															{index + 1}.
															<Input
																value={detail.name}
																onChange={(e) =>
																	updateDetail(
																		sectionType,
																		index,
																		"name",
																		e.target.value,
																	)
																}
																readOnly
															/>
														</TableCell>
														<TableCell>
															{/* <Input
																type="text"
																value={formatNumber(
																	detail.amount,
																	detail.financialType,
																)}
																onChange={(e) => {
																	const value = e.target.value;
																	if (/^[()\d,\-]*$/.test(value)) {
																		const cleaned = value.replace(/,/g, "");
																		updateDetail(
																			sectionType,
																			index,
																			"amount",
																			cleaned,
																		);
																	}
																}}
																className="text-right"
																inputMode="numeric"
															/> */}
															<Input
																type="text"
																value={formatNumber(detail.amount)}
																onChange={(e) => updateDetail(sectionType, index, "amount", e.target.value)}
																className="text-right"
																inputMode="numeric"
															/>
														</TableCell>
														<TableCell>
															<Input
																value={detail.remark}
																onChange={(e) =>
																	updateDetail(
																		sectionType,
																		index,
																		"remark",
																		e.target.value,
																	)
																}
															/>
														</TableCell>
													</TableRow>
												))}
												<TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black [&>*]:border">
													<TableCell>{getSectionTitle(sectionType)}</TableCell>
													<TableCell className="text-right">
														{getSectionSUM(sectionType)}
													</TableCell>
													<TableCell>-</TableCell>
												</TableRow>
											</React.Fragment>
										),
									)}
									<TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
										<TableCell className="whitespace-nowrap border">
											ຜົນໄດ້ຮັບສຸດທິໃນປີ
										</TableCell>
										<TableCell className="text-right border">
											{formatNumber(
												totalOperating +
												totalInitial +
												totalPreTax +
												totalNetOperating +
												totalAfterTax,
											)}
										</TableCell>
										<TableCell className="text-left border">-</TableCell>
									</TableRow>
									<TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
										<TableCell className="whitespace-nowrap border">
											ທີ່ເປັນສ່ວນຂອງ
										</TableCell>
										<TableCell className="text-left border">-</TableCell>
										<TableCell className="text-left border">-</TableCell>
									</TableRow>
									<TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
										<TableCell className="whitespace-nowrap border">
											ພູດສ່ວນ ຂອງຜົນປະໂຫຍດສ່ວນນ້ອຍ(1)
										</TableCell>
										<TableCell className="text-left border">-</TableCell>
										<TableCell className="text-left border">-</TableCell>
									</TableRow>
									<TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
										<TableCell className="whitespace-nowrap border">
											ພູດສ່ວນ ຂອງກຸ່ມ(1)
										</TableCell>
										<TableCell className="text-left border">-</TableCell>
										<TableCell className="text-left border">-</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</div>
						<div className="flex gap-x-1 items-center mt-4">
							<FileUpload
								currentFile={docFile}
								onFileUpload={(file) => {
									setDocFile(file);
								}}
							/>
						</div>
						<div className="flex gap-4 justify-end">
							<Button
								variant={"outline"}
								type="button"
								onClick={() =>
									navigate({ to: "/report/finance/company/result", search: { refId: undefined } })
								}
							>
								<span>ຍົກເລີກ</span>
							</Button>
							<Button
								className="flex gap-2 items-center"
								type="submit"
								disabled={mutation.isPending}
							>
								{mutation.isPending ? (
									<Spinner className="text-white" />
								) : (
									<>
										<Save className="w-4 h-4" />
										ບັນທຶກ
									</>
								)}
							</Button>
						</div>
					</CardContent>
				</form>
			</Card>
		</section>
	);
}

const getSectionTitle = (type: string) => {
	const titles: Record<string, string> = {
		INITIAL: "ຜົນໄດ້ຮັບເບື້ອງຕົ້ນ",
		OPERATING: "ຜົນໄດ້ຮັບໃນທຸລະກິດ",
		PRE_TAX: "ຜົນໄດ້ຮັບ ກ່ອນເສຍອາກອນ",
		NET_OPERATING: "ຜົນໄດ້ຮັບ ສຸດທິຈາກການດໍາເນີນງານ",
		AFTER_TAX: "ຜົນໄດ້ຮັບສັງລວມອື່ນໆ ຫຼັງອາກອນ",
	};
	return titles[type];
};

export default RouteComponent;
